import type {
  CompactResourceDescriptor,
  CompactResourceLifecycle,
  CompactResourceThumbnail,
} from "@/desktop/compact-resource-events";
import { useCompanionStore } from "@/stores/companion-store";
import {
  useWorkToastStore,
  type WorkToastActivityEntry,
  type WorkToastAppRef,
  type WorkToastComputerActivity,
  type WorkToastComputerScreenshot,
  type WorkToastItem,
  type WorkToastState,
} from "@/stores/work-toast-store";
import type { ActivityLabel } from "./activity-label";
import { computerToastKey } from "./computer-toast-key";
import { appRef, fillAppRef, withApp, withoutApp } from "./computer-use-toast-apps";
import { resolveOperatedAppVisual, type AppVisual } from "./work-toast-app-visuals";
import { mergeWorkToastActivityRings, moveWorkToast, pushWorkToastActivity } from "./work-toast-item";
import { observeLiveWorkToastCreation } from "./work-toast-live-events";
import { isComputerWorkToastPinned } from "./work-toast-computer-pin";
import {
  mergeComputerScreenshot, resetPendingComputerScreenshots, stashPendingComputerScreenshot, takePendingComputerScreenshots,
} from "./work-toast-computer-screenshots";

/**
 * Feed from desktop execution-resource frames into work toasts.
 *
 * One card per task: when a desktop frame's executionId already owns an
 * agent / native_worker toast (same key), the frame patches that toast's
 * apps + computer activity + screenshot instead of spawning a second
 * standalone computer card. Direct screen work (no task toast) keeps the
 * standalone card, which carries the agent, project and conversation of its
 * execution; the app in use stays in `computer.appLabel` and `apps`.
 *
 * Explicit app or computer activity stays visible through completion until
 * dismissed. A route-less prompt screenshot remains transient context.
 *
 * A card keeps only its latest screen capture (up to three displays), which
 * stays through later steps until the next capture replaces it. A capture that
 * arrives before its card is held for 30 s.
 */

const appFrameVersions = new Map<string, number>();

/** The latest step per execution that never named its app: the app-frame
 *  version it started at, the app its first lookup added (`introduced`), and
 *  whether a successful finish took over its resolution (`finished`). */
interface UnnamedStep { toolUseId: string; version: number; introduced?: string; finished?: boolean; targeted?: boolean }
const unnamedSteps = new Map<string, UnnamedStep>();

const feedContext = { workToastStore: useWorkToastStore };

type ResourceRoute = NonNullable<CompactResourceDescriptor["route"]>;

interface StartedFrame {
  executionId: string;
  toolUseId: string;
  resource: CompactResourceDescriptor;
  /** Absent on older backends and on the local prompt screenshot. */
  route?: ResourceRoute;
}

/** Agent, project and conversation of a standalone card. Null until its
 *  execution is known: the card is then "Denker" with no project or chat. */
function standaloneIdentity(executionId: string): Pick<WorkToastItem, "agentName" | "projectId" | "conversationId"> | null {
  const execution = useCompanionStore.getState().executions.get(executionId);
  if (!execution) return null;
  return {
    agentName: execution.agentName || "Denker",
    projectId: execution.spaceId ?? null,
    conversationId: execution.conversationId ?? null,
  };
}

/** A card keeps its own latest capture; one without any takes the other's. */
function keepScreenshots(
  own: WorkToastComputerScreenshot[] | undefined,
  other: WorkToastComputerScreenshot[] | null | undefined,
): WorkToastComputerScreenshot[] | undefined {
  return own?.length ? own : (other ?? own);
}

function baseItem(executionId: string, now: number, originDisplayId?: number): WorkToastItem {
  return {
    key: computerToastKey(executionId),
    source: "computer",
    tracked: true,
    state: "working",
    stateBeforeBlocked: null,
    agentName: "Denker",
    projectId: null,
    conversationId: null,
    originDisplayId: originDisplayId ?? null,
    taskId: null,
    nativeSubagentId: null,
    apps: [],
    codingProvider: null,
    openTargets: [],
    // The card's "Working on your screen" line is rendered at display time.
    detail: "",
    resultText: null,
    computerScreenshots: takePendingComputerScreenshots(executionId) ?? [],
    computer: null,
    question: null,
    approval: null,
    stateChangedAt: now,
    dismissed: false,
    ...standaloneIdentity(executionId),
  };
}

/** Merged toast for the same execution: a task toast wins over a new computer toast. */
function ownedTaskItem(executionId: string): WorkToastItem | null {
  const item = useWorkToastStore.getState().items[executionId];
  if (!item || !item.tracked) return null;
  if (item.source !== "agent" && item.source !== "native_worker") return null;
  return item;
}

function nextComputerActivity(
  current: WorkToastComputerActivity | null | undefined,
  patch: Partial<Pick<WorkToastComputerActivity, "appLabel" | "actionKey" | "actionLabel" | "actionActivityLabel" | "fieldNames" | "active">>,
  now: number,
): WorkToastComputerActivity {
  return {
    appLabel: patch.appLabel !== undefined ? patch.appLabel : (current?.appLabel ?? null),
    actionKey: patch.actionKey !== undefined ? patch.actionKey : (current?.actionKey ?? null),
    actionLabel: patch.actionLabel !== undefined ? patch.actionLabel : (current?.actionLabel ?? null),
    // The label always describes the English step next to it.
    actionActivityLabel: patch.actionLabel !== undefined
      ? (patch.actionActivityLabel ?? null)
      : (current?.actionActivityLabel ?? null),
    fieldNames: patch.fieldNames ?? (patch.actionKey ? [] : current?.fieldNames ?? []),
    updatedAt: now,
    active: patch.active ?? current?.active ?? false,
  };
}

/** A step that never named its app takes the app the execution's latest
 *  desktop step worked in (the system's frontmost app only when none was
 *  recorded): agents keep working in one window, and a background action
 *  leaves the user's own app in front. Resolved when the step starts, and
 *  again when it finishes successfully (`correcting`), which replaces what the
 *  first lookup added if the step moved to another app. The app is evidence
 *  only once resolved: patch the ref, the live label, and the step's app part
 *  if the step is still in the ring. A miss adds nothing (D2). */
function resolveOperatedApp(executionId: string, step: UnnamedStep, correcting: boolean): void {
  void resolveOperatedAppVisual(executionId).then((visual: AppVisual | null) => {
    // A later app frame is stronger evidence for the current app. In-flight
    // lookups must never move an older activity row back to the end of the ring.
    if (!visual || appFrameVersions.get(executionId) !== step.version) return;
    if (unnamedSteps.get(executionId) !== step || (!correcting && step.finished)) return;
    const store = useWorkToastStore.getState();
    // Re-resolve the owner: a `running` frame may have adopted the standalone
    // computer toast into its task card while the lookup was in flight.
    const key = ownedTaskItem(executionId)?.key ?? computerToastKey(executionId);
    const item = store.items[key];
    if (!item || item.state !== "working") return;
    const guess = correcting && step.introduced !== visual.appId ? step.introduced : undefined;
    const base = guess ? { ...item, ...withoutApp(item, guess) } : item;
    if (!correcting && !item.apps?.some((ref) => ref.appId === visual.appId)) step.introduced = visual.appId;
    const ref: WorkToastAppRef = { appId: visual.appId, route: "ax", confirmed: true, label: visual.label, iconUrl: visual.iconUrl };
    const activity = item.activity?.map((entry) => entry.id === step.toolUseId
      ? { ...entry, app: visual.label }
      : entry);
    store.patchItem(key, {
      ...withApp(base, ref),
      computer: item.computer ? { ...item.computer, appLabel: visual.label } : item.computer,
      ...(activity ? { activity } : {}),
    });
  });
}

/** Apply one started frame to the toast that owns the execution (task card or
 *  standalone computer card). Desktop frames drive `computer` (live logo +
 *  pin) and write `computer` entries; MCP frames only name apps and fill the
 *  entry's app part — the cursor frame for the same tool id brings the label. */
function applyStartedFrame(key: string, frame: StartedFrame, now: number): void {
  const store = useWorkToastStore.getState();
  const item = store.items[key];
  if (!item) return;
  const { resource, route, toolUseId, executionId } = frame;
  const desktop = route !== "mcp";
  const pending = unnamedSteps.get(executionId);
  if (desktop && pending?.targeted && pending.toolUseId !== toolUseId) unnamedSteps.delete(executionId);
  // Route-less frames keep their preview without pinning the toast.
  const pin = route === "ax" ? { active: true } : {};
  const patch: Partial<WorkToastItem> = {
    computerScreenshots: keepScreenshots(item.computerScreenshots, takePendingComputerScreenshots(executionId)),
    // The execution may have become known after the card was created.
    ...(item.source === "computer" ? standaloneIdentity(executionId) : null),
  };
  const entry: WorkToastActivityEntry = { id: toolUseId, kind: desktop ? "computer" : "tool", updatedAt: now };
  if (resource.kind === "screenshot") {
    // A route-less prompt preview is only context for a future execution.
    // An explicit AX screenshot is computer use in its own right and stays
    // visible until dismissed, even when no later app/action frame arrives.
    if (route === "ax") {
      entry.action = resource.label;
      entry.actionLabel = resource.activityLabel;
      entry.app = item.computer?.appLabel ?? undefined;
      patch.computer = nextComputerActivity(item.computer, {
        actionKey: resource.key, actionLabel: resource.label, actionActivityLabel: resource.activityLabel, active: true,
      }, now);
      store.patchItem(key, patch);
      pushWorkToastActivity(feedContext, key, entry);
    } else {
      store.patchItem(key, { ...patch, stateChangedAt: now });
    }
    return;
  }
  if (resource.kind === "app") {
    const version = (appFrameVersions.get(executionId) ?? 0) + 1;
    appFrameVersions.set(executionId, version);
    const frontmost = desktop && resource.key === "frontmost";
    const targeted = desktop && resource.key === "target";
    if (!frontmost && !targeted) {
      Object.assign(patch, withApp(item, appRef(resource, route ?? "ax")));
      entry.app = resource.label;
    }
    if (desktop) patch.computer = nextComputerActivity(item.computer, { appLabel: targeted ? null : frontmost ? undefined : resource.label, ...pin }, now);
    store.patchItem(key, patch);
    // An unresolved frontmost app has nothing to say; its action frame adds the entry.
    if (entry.app) pushWorkToastActivity(feedContext, key, entry);
    if (frontmost || targeted) {
      const step: UnnamedStep = { toolUseId, version, targeted };
      unnamedSteps.set(executionId, step);
      if (!targeted) resolveOperatedApp(executionId, step, false);
    }
    return;
  }
  entry.action = resource.label;
  entry.actionLabel = resource.activityLabel;
  if (desktop) {
    entry.app = item.computer?.appLabel ?? undefined;
    patch.computer = nextComputerActivity(item.computer, {
      actionKey: resource.key, actionLabel: resource.label, actionActivityLabel: resource.activityLabel, ...pin,
    }, now);
  }
  store.patchItem(key, patch);
  pushWorkToastActivity(feedContext, key, entry);
}

/**
 * Fold a standalone computer toast into a newly arrived task toast.
 * Handles the race where desktop frames land before `execution.status`
 * running creates the agent / native_worker item.
 */
export function adoptComputerToastIntoTask(taskKey: string): void {
  const store = useWorkToastStore.getState();
  const taskItem = store.items[taskKey];
  const computerItem = store.items[computerToastKey(taskKey)];
  if (!taskItem || !computerItem) return;
  if (taskItem.source !== "agent" && taskItem.source !== "native_worker") return;
  const mergedApps = [...(taskItem.apps ?? [])];
  for (const ref of computerItem.apps ?? []) {
    if (!mergedApps.some((current) => current.appId === ref.appId)) mergedApps.push(ref);
  }
  const mergedTargets = [...(taskItem.openTargets ?? [])];
  for (const target of computerItem.openTargets ?? []) {
    const existingIndex = mergedTargets.findIndex((current) => current.appId === target.appId);
    const existing = existingIndex === -1 ? undefined : mergedTargets[existingIndex];
    if (!existing) {
      mergedTargets.push(target);
    } else if (!existing.uri && target.uri) {
      // Same app appeared with and without a uri (e.g. a url-less app-kind
      // target followed by a resource target): keep the one that has one.
      mergedTargets[existingIndex] = target;
    }
  }
  store.patchItem(taskKey, {
    originDisplayId: taskItem.originDisplayId ?? computerItem.originDisplayId ?? null,
    apps: mergedApps.slice(-8),
    openTargets: mergedTargets,
    computerScreenshots: keepScreenshots(taskItem.computerScreenshots, computerItem.computerScreenshots),
    computer: computerItem.computer ?? taskItem.computer ?? null,
    activity: mergeWorkToastActivityRings(taskItem.activity, computerItem.activity),
    stateChangedAt: Math.max(taskItem.stateChangedAt, computerItem.stateChangedAt),
  });
  store.removeItem(computerItem.key);
}

export function applyComputerUseLifecycle(event: CompactResourceLifecycle, now: number): void {
  const before = useWorkToastStore.getState().items;
  applyLifecycle(event, now);
  const after = useWorkToastStore.getState().items;
  if (Object.values(after).some((item) => isComputerWorkToastPinned(item) && !isComputerWorkToastPinned(before[item.key]))) {
    observeLiveWorkToastCreation(before, after);
  }
}

function applyLifecycle(event: CompactResourceLifecycle, now: number): void {
  const status = useCompanionStore.getState().executions.get(event.executionId)?.status;
  const terminalState = status && TERMINAL_EXECUTION_STATE[status];
  if ((event.phase === "field_edit" || event.phase === "started") && terminalState) {
    settleComputerUseToast(event.executionId, now);
    return;
  }
  const store = useWorkToastStore.getState();
  const prior = ownedTaskItem(event.executionId) ?? store.items[computerToastKey(event.executionId)];
  if (prior && event.originDisplayId !== undefined && prior.originDisplayId !== event.originDisplayId) {
    store.patchItem(prior.key, { originDisplayId: event.originDisplayId });
  }
  if (event.phase === "field_edit") {
    const appLabel = event.appLabel?.trim();
    const fieldName = event.fieldName?.trim();
    if (!fieldName || fieldName.length > 160
      || [...fieldName].some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)
      || (event.editStatus !== "verified" && event.editStatus !== "uncertain")) return;
    const owned = ownedTaskItem(event.executionId);
    const key = owned?.key ?? computerToastKey(event.executionId);
    if (!store.items[key]) store.upsertItem(baseItem(event.executionId, now, event.originDisplayId));
    const item = useWorkToastStore.getState().items[key];
    if (!item || item.state !== "working") return;
    const verified = event.editStatus === "verified";
    const fieldNames = verified
      ? [...new Set([...(item.computer?.fieldNames ?? []), fieldName])].slice(-8)
      : item.computer?.fieldNames ?? [];
    const label = verified ? `Filled ${fieldName}` : `Check ${fieldName}`;
    const activityLabel: ActivityLabel = {
      key: verified ? "ui.filled_field" : "ui.check_field", args: { field: fieldName },
    };
    // An app that cannot be named leaves the toast's apps and live app alone.
    const app = appLabel ? fillAppRef(item.apps, appLabel) : null;
    store.patchItem(key, {
      ...(app ? withApp(item, app) : null),
      computer: nextComputerActivity(item.computer, {
        appLabel: appLabel || undefined, actionKey: "field-edit", actionLabel: label, actionActivityLabel: activityLabel,
        fieldNames, active: true,
      }, now),
    });
    pushWorkToastActivity(feedContext, key, {
      id: event.toolUseId ?? `${event.executionId}:${now}`, kind: "computer",
      app: appLabel || (item.computer?.appLabel ?? undefined), action: label, actionLabel: activityLabel, updatedAt: now,
    });
    return;
  }
  if (event.phase === "execution_bound") {
    if (terminalState) settleComputerUseToast(event.executionId, now);
    const requestId = event.clientRequestId;
    if (!requestId || requestId === event.executionId) return;
    const previewKey = computerToastKey(requestId);
    const preview = store.items[previewKey];
    if (!preview) return;
    const targetKey = computerToastKey(event.executionId);
    const target = store.items[targetKey];
    const owner = ownedTaskItem(event.executionId);
    const bound = owner ?? target;
    if (bound) {
      store.patchItem(bound.key, {
        ...(terminalState && bound.source === "computer" ? standaloneIdentity(event.executionId) : null),
        computerScreenshots: keepScreenshots(bound.computerScreenshots, preview.computerScreenshots),
        computer: bound.computer ?? preview.computer,
        activity: mergeWorkToastActivityRings(bound.activity, preview.activity),
      });
    } else {
      store.upsertItem({
        ...preview, key: targetKey,
        ...(terminalState ? {
          ...standaloneIdentity(event.executionId), state: terminalState, detail: "", stateChangedAt: now,
        } : null),
      });
    }
    store.removeItem(previewKey);
    if (terminalState) settleComputerUseToast(event.executionId, now);
    return;
  }
  if (event.phase === "execution_finished") {
    appFrameVersions.delete(event.executionId);
    unnamedSteps.delete(event.executionId);
    // The reducer publishes this before writing the terminal execution
    // status; defer one microtask so the settle reads the fresh status.
    // A merged task toast settles through its own execution.status path.
    const executionId = event.executionId;
    void Promise.resolve().then(() => settleComputerUseToast(executionId, Date.now()));
    return;
  }
  if (event.phase === "finished") {
    // An errored step keeps what its start showed.
    const step = unnamedSteps.get(event.executionId);
    if (event.resource?.kind === "app" && event.resource.key === "target" && step?.targeted &&
      step.toolUseId === event.toolUseId && appFrameVersions.get(event.executionId) === step.version &&
      !step.finished) {
      step.finished = true;
      const appName = event.resource.appName?.trim();
      if (event.failed || !appName || appName.length > 200 ||
        Array.from(appName).some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)) return;
      const store = useWorkToastStore.getState();
      const key = ownedTaskItem(event.executionId)?.key ?? computerToastKey(event.executionId);
      const item = store.items[key];
      if (item?.state === "working") {
        store.patchItem(key, {
          ...withApp(item, fillAppRef(item.apps, appName)),
          computer: item.computer ? { ...item.computer, appLabel: appName } : item.computer,
          activity: item.activity?.map((entry) => entry.id === step.toolUseId ? { ...entry, app: appName } : entry),
        });
      }
    }
    if (!event.failed && event.resource?.key === "frontmost" && step && !step.targeted && step.toolUseId === event.toolUseId) {
      step.finished = true;
      resolveOperatedApp(event.executionId, step, true);
    }
    return;
  }
  if (event.phase !== "started" || !event.resource) return;
  const frame: StartedFrame = {
    executionId: event.executionId,
    toolUseId: event.toolUseId ?? `${event.executionId}:${now}`,
    resource: event.resource,
    route: event.resource.route,
  };
  // One card per task: patch the owning task toast when it already exists
  // (title/task context stay untouched — only apps + live activity merge).
  const owned = ownedTaskItem(frame.executionId);
  if (owned) {
    if (owned.state === "working") applyStartedFrame(owned.key, frame, now);
    return;
  }
  const key = computerToastKey(frame.executionId);
  const existing = store.items[key];
  if (existing && existing.state !== "working") return;
  if (!existing) store.upsertItem(baseItem(frame.executionId, now, event.originDisplayId));
  applyStartedFrame(key, frame, now);
}
const TERMINAL_EXECUTION_STATE: Record<string, WorkToastState> = {
  completed: "done",
  failed: "failed",
  cancelled: "stopped",
  paused: "stopped",
};

export function settleComputerUseToast(executionId: string, now: number): void {
  const store = useWorkToastStore.getState();
  const key = computerToastKey(executionId);
  const item = store.items[key];
  if (!item || item.state !== "working") return;
  const status = useCompanionStore.getState().executions.get(executionId)?.status;
  const state = status ? TERMINAL_EXECUTION_STATE[status] : undefined;
  appFrameVersions.delete(executionId);
  unnamedSteps.delete(executionId);
  // A missing or unknown status after reconnect proves neither success nor a
  // stop: the card leaves instead of claiming an outcome.
  if (!state) {
    store.removeItem(key);
    return;
  }
  // "Working on your screen" is not a result: the settled card says its state.
  moveWorkToast({ workToastStore: useWorkToastStore }, item, state, now, { detail: "" });
}

export function applyComputerUseThumbnail(thumbnail: CompactResourceThumbnail): void {
  const store = useWorkToastStore.getState();
  // A merged task toast owns this execution: keep one card, patch it.
  const item = ownedTaskItem(thumbnail.executionId) ?? store.items[computerToastKey(thumbnail.executionId)];
  if (!item) {
    stashPendingComputerScreenshot(thumbnail);
    return;
  }
  store.patchItem(item.key, { computerScreenshots: mergeComputerScreenshot(item.computerScreenshots, thumbnail) });
}

export function resetComputerUseToastFeedForTests(): void {
  resetPendingComputerScreenshots();
  appFrameVersions.clear();
  unnamedSteps.clear();
}
